import { Check, ListChecks } from "lucide-react";
import { useState } from "react";
import { type TaskBoard, type TaskCategory } from "../types";
import { Meter, ResourceIcon } from "../ui/Resources";
import { formatTaskReset } from "../ui/format";

export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  main: "主线",
  challenge: "挑战",
  daily: "每日",
  weekly: "每周",
};

export function TaskPanel({
  board,
  busy,
  onClaim,
  onClaimAll,
}: {
  board: TaskBoard;
  busy: boolean;
  onClaim: (taskId: string) => void;
  onClaimAll: () => void;
}) {
  const [category, setCategory] = useState<TaskCategory>("main");
  const visible = board.tasks.filter((task) => task.category === category);
  const claimableCount = board.tasks.filter((task) => task.claimable).length;
  const resetAt =
    category === "daily" ? board.dailyResetAt : category === "weekly" ? board.weeklyResetAt : 0;
  return (
    <section className="tasks-panel" aria-labelledby="tasks-title">
      <header>
        <div>
          <ListChecks size={17} />
          <strong id="tasks-title">任务</strong>
        </div>
        <div className="task-header-actions">
          <button
            type="button"
            className="secondary-command compact"
            disabled={busy || claimableCount === 0}
            onClick={onClaimAll}
            title={claimableCount > 0 ? `领取 ${claimableCount} 项已完成任务` : "暂无可领取任务"}
          >
            <Check size={14} />
            全部领取
          </button>
          <span className="task-wallet" title="结晶">
            <ResourceIcon kind="crystal" size={15} />
            <strong>{board.crystal.toLocaleString("zh-CN")}</strong>
          </span>
        </div>
      </header>
      <div className="task-tabs" role="tablist" aria-label="任务分类">
        {(Object.keys(TASK_CATEGORY_LABELS) as TaskCategory[]).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={category === item}
            className={category === item ? "is-active" : ""}
            onClick={() => setCategory(item)}
          >
            {TASK_CATEGORY_LABELS[item]}
          </button>
        ))}
      </div>
      {resetAt > 0 && <div className="task-reset">{formatTaskReset(resetAt)} 重置</div>}
      <div className="task-list">
        {visible.map((task) => {
          const displayedProgress = Math.min(task.progress, task.target);
          return (
            <article
              className={`task-row${task.claimed ? " is-claimed" : task.claimable ? " is-claimable" : ""}`}
              key={task.id}
            >
              <div className="task-copy">
                <strong>{task.name}</strong>
                <small>{task.description}</small>
              </div>
              <span className="task-reward" title="结晶">
                <ResourceIcon kind="crystal" size={13} />
                {task.crystalReward}
              </span>
              <div className="task-progress">
                <Meter value={(displayedProgress / task.target) * 100} tone="xp" />
                <span>
                  {displayedProgress.toLocaleString("zh-CN")} /{" "}
                  {task.target.toLocaleString("zh-CN")}
                </span>
              </div>
              {task.claimed ? (
                <span className="task-state">
                  <Check size={14} />
                  已领取
                </span>
              ) : task.claimable ? (
                <button
                  type="button"
                  className="primary-command compact"
                  disabled={busy}
                  onClick={() => onClaim(task.id)}
                >
                  <ResourceIcon kind="crystal" size={14} />
                  领取
                </button>
              ) : (
                <span className="task-state">进行中</span>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
